import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from '../../App.module.css';
import api from '../../api/axios';
import { FaTimes, FaPen } from 'react-icons/fa';
import { NotificationContainer, NotificationManager } from 'react-notifications';
import 'react-notifications/lib/notifications.css';
import '../../css/notifications.css';
import { isAuthenticated } from '../../index';
import notifications from '../../components/notifications';
import ListaFormularios from "../../components/ListaFormularios";
import { PacmanLoader } from 'react-spinners';

function Pesquisa({ query = "" }) {
  const appOrigin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";

  const [noResultToastShown, setNoResultToastShown] = useState(false);
  const [notificationType, setNotificationType] = useState(0);
  const { userPermission, userStatus, userCpf } = isAuthenticated();

  const [filtroProdutor, setFiltroProdutor] = useState(0);
  const [filtroStatusProd, setFiltroStatusProd] = useState(0);

  const [dataReady, setDataReady] = useState(false);
  const [usersListProd, setUsersListProd] = useState([]);
  const [filteredDataProd, setFilteredDataProd] = useState([]);

  const textareaRef = useRef(null);

  const [isModalOpenForm, setIsModalOpenForm] = useState(false);
  const [currentFormContent, setCurrentFormContent] = useState('');
  const [currentFormId, setCurrentFormId] = useState('');

  const [isModalOpenAnalise, setIsModalOpenAnalise] = useState(false);

  const [filtroVendedorProd] = useState(0);
  const [inputValue] = useState('');
  const [currentAnaliseContent] = useState('');

  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setSearchTerm(query);
  }, [query]);

  useEffect(() => {
    if (notificationType && notifications[notificationType]) {
      notifications[notificationType]();
      setNotificationType(0);
    }
  }, [notificationType]);

  const getUsers = useCallback(async () => {
    try {
      const res = await api.get(
        `/usuariosComFormularios?userPermission=${userPermission}&CPF=${userCpf}`
      );
      const { users } = res.data;

      if (!users || !Array.isArray(users)) {
        console.error('Erro: `users` não é um array válido:', users);
        setUsersListProd([]);
        setFilteredDataProd([]);
        NotificationManager.error('Resposta inválida ao carregar usuários.', 'Erro');
        return;
      }

      setUsersListProd(users);
      setFilteredDataProd(users);
    } catch (error) {
      console.error('Erro ao buscar usuários e formulários:', error);
      NotificationManager.error('Não foi possível carregar usuários e formulários.', 'Erro');
    } finally {
      setDataReady(true);
    }
  }, [userPermission, userCpf]);

  const getVendedoresProdutores = useCallback(async () => {
    try {
      const resVendedoresProdutores = await api.get('/vendedores-produtores');

      if (
        resVendedoresProdutores.data &&
        Array.isArray(resVendedoresProdutores.data.data)
      ) {
        // sem uso direto neste componente
      } else {
        console.error('Formato inesperado da resposta:', resVendedoresProdutores.data);
        NotificationManager.error('Erro ao carregar vendedores e produtores.', 'Erro');
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      NotificationManager.error('Erro ao carregar dados.', 'Erro');
    }
  }, []);

  useEffect(() => {
    if (userPermission === 2) {
      setFiltroProdutor(userStatus);
      setFiltroStatusProd(-2);
    }

    getVendedoresProdutores();
    getUsers();
  }, [getUsers, getVendedoresProdutores, userPermission, userStatus]);

  const normalize = (text) =>
    (text ?? "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\p{L}\p{N}]+/gu, "")
      .toLowerCase();

  const flattenValues = useCallback((val) => {
    if (val === null || val === undefined) return "";
    if (Array.isArray(val)) return val.map(flattenValues).join(" ");
    if (typeof val === "object") return Object.values(val).map(flattenValues).join(" ");
    return String(val);
  }, []);

  useEffect(() => {
    const normalizedSearch = normalize(searchTerm);

    const next = usersListProd.filter((user) => {
      const statusMatch =
        filtroStatusProd === 0 ||
        user.produzirStatus === filtroStatusProd ||
        (filtroStatusProd === -2 &&
          (user.produzirStatus === 0 || user.produzirStatus === 2));

      const vendedorMatch =
        filtroVendedorProd === 0 || user.vendedor === filtroVendedorProd;

      const produtorMatch =
        filtroProdutor === 0 || user.produtor === filtroProdutor;

      const login = normalize(user.login || "");
      const vendedor = normalize(user.vendedor || "");
      const produtor = normalize(user.produtor || "");
      const nomeCliente = normalize(user.nomeCompleto || user.Nome || "");
      const contatoCliente = normalize(user.contato || "");

      const forms = Array.isArray(user.formularios) ? user.formularios : [];
      const formsBlob = forms
        .map((f) => {
          const ds160 = normalize((f.ds160_id ?? "").toString());
          let data = {};
          try {
            data = JSON.parse(f.formularioData || "{}");
          } catch {
            data = {};
          }
          const flat = normalize(flattenValues(data));
          return [ds160, flat].join(" ");
        })
        .join(" ");

      const haystack = [
        login,
        vendedor,
        produtor,
        nomeCliente,
        contatoCliente,
        formsBlob,
      ].join(" ");

      const searchMatch = !normalizedSearch || haystack.includes(normalizedSearch);

      return statusMatch && vendedorMatch && produtorMatch && searchMatch;
    });

    setFilteredDataProd(next);
  }, [
    usersListProd,
    filtroVendedorProd,
    filtroProdutor,
    filtroStatusProd,
    searchTerm,
    flattenValues,
  ]);

  useEffect(() => {
    if (!dataReady) return;

    const someFilterApplied =
      (searchTerm && searchTerm.trim().length > 0) ||
      filtroVendedorProd !== 0 ||
      filtroProdutor !== 0 ||
      filtroStatusProd !== 0;

    if (someFilterApplied && filteredDataProd.length === 0) {
      if (!noResultToastShown) {
        NotificationManager.info(
          'Nenhum resultado encontrado. Ajuste a busca ou os filtros.',
          'Sem resultados'
        );
        setNoResultToastShown(true);
      }
    } else if (noResultToastShown) {
      setNoResultToastShown(false);
    }
  }, [
    filteredDataProd.length,
    searchTerm,
    filtroVendedorProd,
    filtroProdutor,
    filtroStatusProd,
    noResultToastShown,
    dataReady,
  ]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        400
      )}px`;
    }
  }, [inputValue]);

  const renderFormContent = (content) => {
    try {
      const parsedContent =
        typeof content === "string" ? JSON.parse(content) : content;

      const informed = [];
      const notInformed = [];

      Object.entries(parsedContent).forEach(([key, value]) => {
        const isEmpty =
          value === null ||
          value === undefined ||
          value === "" ||
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === "object" &&
            !Array.isArray(value) &&
            Object.keys(value).length === 0);

        if (isEmpty) {
          notInformed.push(key);
        } else {
          const displayValue = Array.isArray(value)
            ? value.join(", ")
            : typeof value === "object"
              ? JSON.stringify(value)
              : value;

          informed.push(
            <div key={key} style={{ marginBottom: "10px" }}>
              <strong>{key}:</strong> {displayValue}
            </div>
          );
        }
      });

      return (
        <div>
          <div>
            <h3>Informações Preenchidas</h3>
            {informed.length > 0 ? informed : <p>Nenhuma informação preenchida.</p>}
          </div>

          {notInformed.length > 0 && (
            <div
              style={{
                marginTop: "20px",
                padding: "10px",
                border: "1px solid #ccc",
                borderRadius: "5px"
              }}
            >
              <h3>Perguntas Não Informadas</h3>
              {notInformed.map((key) => (
                <div key={key} style={{ marginBottom: "5px" }}>
                  <strong>{key}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    } catch (error) {
      console.error("Erro ao renderizar conteúdo do formulário:", error);
      return <div>Erro ao exibir o conteúdo</div>;
    }
  };

  const groupColors = ['#8BAED3', '#F29098', '#808EA3', '#C0ECAE'];

  if (!dataReady) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "40vh",
          flexDirection: "column",
          gap: "16px"
        }}
      >
        <PacmanLoader color="#162768" size={40} />
        <p style={{ fontSize: 16, color: "#162768" }}>Carregando dados...</p>
      </div>
    );
  }

  return (
    <div className={styles.app}>
      <NotificationContainer />

      <div className={styles.cadastroClienteContainerPesquisa}>
        <div style={{ display: "flex", width: "100%" }}>
          <div style={{ height: "100%" }}>
            <ListaFormularios
              users={filteredDataProd.filter((user) => user.status !== 1)}
              groupColors={groupColors}
              onClickVer={async (user) => {
                try {
                  const res = await api.get(`/cliente/token-por-cliente/${user.clienteId}`);

                  if (res.data?.status && res.data?.token) {
                    window.location.href = `${origin}/formulario/${res.data.token}`;
                  } else {
                    window.location.href = `${origin}/formularios/${user.clienteId}`;
                  }
                } catch (error) {
                  console.error("Erro ao verificar token do cliente:", error);
                  window.location.href = `${origin}/formularios/${user.clienteId}`;
                }
              }}
              onClickAnalise={() => { }}
              setCurrentFormContent={setCurrentFormContent}
              setCurrentFormId={setCurrentFormId}
              setIsModalOpenForm={setIsModalOpenForm}
              tipo="pendencias"
            />
          </div>
        </div>
      </div>

      {isModalOpenAnalise && (
        <div className={styles.modalProducaoForm}>
          <div className={styles.modalContentProducaoForm}>
            <div
              className={styles.closeForm}
              onClick={() => setIsModalOpenAnalise(false)}
            >
              &times; Fechar
            </div>
            <h2>Conteúdo da Análise de Perfil</h2>
            <p>{currentAnaliseContent}</p>
          </div>
        </div>
      )}

      {isModalOpenForm && (
        <div className={styles.modalProducaoForm}>
          <div className={styles.modalContentProducaoForm}>
            <div
              className={styles.closeForm}
              onClick={() => setIsModalOpenForm(false)}
            >
              <FaTimes /> Fechar
            </div>
            <a href={`${appOrigin}/preencher/${currentFormId}`}>
              <div
                className={styles.closeForm}
                style={{ backgroundColor: "var(--corOn)", color: 'black' }}
              >
                <FaPen /> Editar
              </div>
            </a>
            <h2>Conteúdo da DS-160</h2>
            <p>Form ID: {currentFormId}</p>
            <div>{renderFormContent(currentFormContent)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Pesquisa;